import type { ReactNode } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { ExternalLink } from '@uikit';

import type { ItemsProps, ProjectContentTableOfContentTab } from '../types';
import { gallery, membershipTiers, step1, step2 } from './constants';

export type SubTitleProps = {
  children: ReactNode;
  className?: string;
};

export type ListProps = {
  isOrdered?: boolean;
  items: Array<ItemsProps>;
  className?: string;
};

const SubTitle = ({ children, className }: SubTitleProps) => {
  return <h3 className={classNames('text-2xl font-semibold', className)}>{children}</h3>;
};

const List = ({ isOrdered, items, className }: ListProps) => {
  const ListTag = isOrdered ? 'ol' : 'ul';

  return (
    <ListTag className={classNames(isOrdered ? 'list-decimal' : 'list-disc', 'pl-6', className)}>
      {items.map(({ id, content, item }) => {
        if (Array.isArray(item)) {
          return (
            <li key={id} className="font-normal">
              {content}
              <ul className="list-disc pl-6">
                {item.map(({ id: subId, subItem }) => (
                  <li key={subId}>{subItem}</li>
                ))}
              </ul>
            </li>
          );
        } else {
          return <li key={id}>{item}</li>;
        }
      })}
    </ListTag>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'before-intro',
    content: (
      <p className="mb-6 max-sm:text-sm font-normal">
        Magic Square is partnering with Wisdomise to distribute{' '}
        <span className="font-bold">$10,000 worth of WSDM tokens</span> through a two-phase
        campaign. The first phase is a{' '}
        <span className="font-bold">
          <ExternalLink
            href="https://docs.magicsquare.io/magic-launchpad/for-users/magic-drops"
            className="text-base-text-blue underline"
          >
            Magic Drop
          </ExternalLink>
        </span>
        , where SQRp holders can deposit points to claim their share of{' '}
        <span className="font-bold">$5,000 in WSDM tokens</span>. The second phase is a{' '}
        <span className="font-bold">raffle</span>, where users who swap at least{' '}
        <span className="font-bold">
          $100 into WSDM on{' '}
          <ExternalLink
            className="text-base-text-blue underline"
            href="https://magicsquare.io/swap"
          >
            Magic Swap
          </ExternalLink>
        </span>{' '}
        during the campaign period will be entered to win a portion of an additional{' '}
        <span className="font-bold">$5,000 in rewards.</span>
      </p>
    ),
  },
  {
    id: 'intro',
    title: 'Wisdomise Introduction',
    content: (
      <>
        <p className="mb-6 max-sm:text-sm font-normal">
          <span className="font-bold">
            <ExternalLink href="https://wisdomise.com/en" className="text-base-text-blue underline">
              Wisdomise
            </ExternalLink>
          </span>{' '}
          is an AI-powered financial ecosystem that simplifies investing, trading, and wealth
          management. By leveraging artificial intelligence, Wisdomise provides cutting-edge tools
          that help users optimize their financial decisions and strategies. With a focus on
          accessibility and efficiency, Wisdomise is revolutionizing the way users interact with
          digital assets and financial markets.
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="pb-10"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className="w-4/5 px-1">
                <ImageProxy
                  alt={`wisdomise-${index + 1}`}
                  width={640}
                  height={385}
                  quality={100}
                  className="w-full h-auto select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'about',
    title: 'About WSDM Token',
    content: (
      <>
        <ul className="mb-4 list-none pl-0 max-sm:text-sm font-normal">
          <li className="mb-4">
            The{' '}
            <span className="font-bold">
              <ExternalLink
                href="https://wisdomise.com/en/token"
                className="text-base-text-blue underline"
              >
                WSDM token
              </ExternalLink>
            </span>{' '}
            powers the Wisdomise ecosystem, enabling access to AI-driven financial tools and
            services.
          </li>
          <li className="mb-4">
            Ticker: <span className="font-medium">WSDM</span>
          </li>
          <li className="mb-4">
            Blockchain: <span className="font-medium">Polygon</span>
          </li>
          <li className="mb-4">
            Total Token Supply: <span className="font-medium">1,000,000,000 WSDM</span>
          </li>
          <li className="mb-4">
            Token Price: <span className="font-medium">Approximately $0.005 per WSDM</span>
          </li>
          <li className="mb-4">
            Total Tokens in Magic Drop:{' '}
            <span className="font-medium">1,000,000 WSDM (Worth $5,000)</span>
          </li>
          <li className="mb-4">
            Total Tokens in Raffle:{' '}
            <span className="font-medium">1,000,000 WSDM (Worth $5,000)</span>
          </li>
          <li>
            Contract Address:{' '}
            <span className="font-bold">0x5F2F8818002dc64753daeDF4A6CB2CcB757CD220</span>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'campaign-details',
    title: 'Campaign Details',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          This campaign consists of two distinct phases, offering two different ways for users to
          earn free WSDM tokens.
        </p>
        <SubTitle className="mb-4 max-sm:text-sm font-normal">Phase 1 – WSDM Magic Drop</SubTitle>
        <p className="mb-4 max-sm:text-sm font-normal">
          The <span className="font-bold">WSDM Magic Drop</span> is a token distribution event where
          users can{' '}
          <span className="font-bold">
            deposit SQRp Points to claim a share of the $5,000 WSDM pool.
          </span>{' '}
          This drop is <span className="font-bold">pro-rata</span>, meaning the amount of WSDM each
          participant receives depends on the total number of SQRp Points deposited by all users.
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          Each participant can deposit up to a set limit based on their{' '}
          <span className="font-bold">
            <ExternalLink
              href="https://docs.magicsquare.io/magic-store/for-users/magic-memberships"
              className="text-base-text-blue underline"
            >
              Magic Store membership tier:
            </ExternalLink>
          </span>
        </p>
        <List items={membershipTiers} className="mb-4 max-sm:text-sm font-normal" />
        <p className="mb-4 max-sm:text-sm font-normal">
          The deposit period runs from{' '}
          <span className="font-bold">
            Monday, March 17th at 11 AM UTC to Wednesday, March 19th at 3 PM UTC.
          </span>{' '}
          At the end of the deposit window, the{' '}
          <span className="font-bold">
            total SQRp Points deposited across all users will determine the final allocation per
            participant.
          </span>{' '}
          The more points a user deposits relative to the total pool, the greater their share of the
          WSDM rewards.
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          After the campaign ends, WSDM rewards will{' '}
          <span className="font-bold">
            be claimable directly from Magic Launchpad on March 20th.
          </span>
        </p>
        <SubTitle className="mb-4 max-sm:text-sm font-normal">Phase 2 – WSDM Swap Raffle</SubTitle>
        <p className="mb-4 max-sm:text-sm font-normal">
          The <span className="font-bold">WSDM Swap Raffle</span> provides an additional opportunity
          for users to win free tokens by swapping WSDM on{' '}
          <span className="font-bold">Magic Swap.</span> This is{' '}
          <span className="font-bold">not a trading competition</span> but rather a raffle, where
          all participants who swap at least <span className="font-bold">$100 into WSDM</span>{' '}
          during the campaign period are automatically entered.
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          At the end of the campaign, <span className="font-bold">50 winners</span> will be randomly
          selected, and each will receive{' '}
          <span className="font-bold">$100 worth of WSDM tokens.</span> This ensures fair
          distribution and encourages participation regardless of trading volume.
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          The{' '}
          <span className="font-bold">
            swap period runs from Friday, March 21st to Friday, March 28th.
          </span>{' '}
          Winners will be announced after the campaign ends, and rewards will be{' '}
          <span className="font-bold">airdropped directly to their wallets on March 31st.</span>
        </p>
      </>
    ),
  },
  {
    id: 'steps-to-claim-rewards',
    title: 'Steps to Claim Rewards',
    content: (
      <>
        <SubTitle className="mb-4 max-sm:text-sm font-normal">Magic Drop (SQRp Holders)</SubTitle>
        <List isOrdered items={step1} className="mb-6 max-sm:text-sm font-normal" />
        <SubTitle className="mb-4 max-sm:text-sm font-normal">
          WSDM Swap Raffle (Magic Swap Participants)
        </SubTitle>
        <List isOrdered items={step2} className="max-sm:text-sm font-normal" />
      </>
    ),
  },
];
